import { cardColors } from '@juvare/common-web-ui/Cards/cardColors';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { Box, Typography } from '@mui/material';
import SvgIcon from '@mui/material/SvgIcon';
import React from 'react';
import { useAlertConfig, useAlertUiTranslation } from '../../../../../hooks';
import { getAlertIcon } from '../../../../../utils';
import { ScheduleNotificationControls } from '../Components/ScheduleNotificationControls';
import { useDeliveryMethodPanel } from './Panels/useDeliveryMethodPanel';
import { useDeliveryOrderPanel } from './Panels/useDeliveryOrderPanel';
import { useDetailsPanel } from './Panels/useDetailsPanel';
import { useMessagesPanel } from './Panels/useMessagesPanel';
import { usePhinMessagingPanel } from './Panels/usePhinMessagingPanel';
import { useAddRecipientsStep } from './useAddRecipientsStep';
export function useReviewStep(mode, onBackToEdit, onSubmit, onSaveTemplate, phinSelected, alertIcon, followUp, generateUniqueIdentifier) {
    const { t } = useAlertUiTranslation();
    const { onGetAdditionalWizardStep } = useAlertConfig();
    const panelConfig = { mode: 'review' };
    const details = useDetailsPanel(panelConfig);
    const addRecipients = useAddRecipientsStep(mode, Object.assign({ hideHeader: true }, panelConfig), followUp).form.children;
    const messages = useMessagesPanel(panelConfig);
    const deliveryMethods = useDeliveryMethodPanel(Object.assign(Object.assign({}, panelConfig), { phinSelected: phinSelected }));
    const phinMessaging = usePhinMessagingPanel(Object.assign(Object.assign({}, panelConfig), { generateUniqueIdentifier: generateUniqueIdentifier }));
    const deliveryOrder = useDeliveryOrderPanel(panelConfig);
    const AlertIcon = getAlertIcon(alertIcon === null || alertIcon === void 0 ? void 0 : alertIcon.icon);
    const alertColor = alertIcon && (alertIcon === null || alertIcon === void 0 ? void 0 : alertIcon.color) ? cardColors[alertIcon.color] : themeColors.accent.grey.boldest;
    const additionalWizardStep = onGetAdditionalWizardStep
        ? [{ type: 'form.panel', key: 'customAlertData', children: onGetAdditionalWizardStep({ mode: 'review' }).formChildren }]
        : [];
    return {
        label: mode === 'notification' ? t('pages.edit.reviewAndSend') : t('common.reviewAndSave'),
        form: {
            type: 'form',
            children: [
                {
                    type: 'form.content',
                    render: (handler) => {
                        var _a;
                        return (React.createElement(Box, { sx: { display: 'flex', gap: 2, alignItems: 'center' } },
                            React.createElement(SvgIcon, { sx: {
                                    padding: 1.5,
                                    fontSize: 64,
                                    borderRadius: '50%',
                                    border: 1,
                                    color: alertColor,
                                    borderColor: alertColor,
                                }, "data-test": 'alert-icon' },
                                React.createElement(AlertIcon, null)),
                            React.createElement(Typography, { variant: 'h1' }, (_a = handler.formData) === null || _a === void 0 ? void 0 : _a.subject)));
                    },
                },
                {
                    type: 'alert.customLayout',
                    children: [
                        {
                            type: 'form.panel',
                            key: '!alertPanel',
                            children: [
                                details,
                                ...addRecipients,
                                deliveryMethods,
                                ...messages,
                                ...additionalWizardStep,
                                phinMessaging,
                                deliveryOrder,
                            ],
                        },
                        {
                            type: 'form.panel',
                            children: [
                                ...createScheduleForm(mode, t),
                                {
                                    type: 'form.content',
                                    render: (handler) => (React.createElement(ScheduleNotificationControls, { mode: mode, handler: handler, onBackToEdit: onBackToEdit, onSaveTemplate: onSaveTemplate, onSubmit: onSubmit })),
                                },
                            ],
                        },
                    ],
                    rootSx: {
                        display: 'grid',
                        gridTemplateColumns: { xs: '100%', lg: '70% 1fr', xl: '70% 1fr' },
                        gap: 3,
                    },
                    childrenSx: [
                        {},
                        {
                            position: { xs: 'static', lg: 'sticky' },
                            top: 0,
                            alignSelf: 'start',
                            p: 2,
                            borderRadius: 2,
                            boxShadow: (theme) => theme.shadows[4],
                        },
                    ],
                },
            ],
        },
    };
}
function createScheduleForm(mode, t) {
    return [
        {
            type: 'form.panel',
            style: {
                surface: 'level1',
            },
            children: [
                {
                    type: 'form.content',
                    html: `<p class="subtitle1">${t('wizard.schedule.title')}</p>`,
                },
                {
                    type: 'form.radio',
                    key: 'sendType',
                    values: [
                        {
                            value: 'immediately',
                            label: t('wizard.schedule.immediately'),
                        },
                        {
                            value: 'schedule',
                            label: t('wizard.schedule.schedule'),
                        },
                    ],
                    defaultValue: 'immediately',
                    event: {
                        onChange: (value, handler) => {
                            // TODO cannot use onChange here, it does not work when loading data
                            const dateTimePicker = handler.up().down('sendDateTime');
                            const currentDate = Date.now();
                            const roundedDate = roundUpToNearestFifteenMinutes(currentDate);
                            if (dateTimePicker) {
                                if (value !== undefined && value === 'schedule') {
                                    dateTimePicker.setData(roundedDate);
                                    dateTimePicker.config.disabled = false;
                                    dateTimePicker.config.validate.required = true;
                                }
                                else {
                                    dateTimePicker.setData(undefined);
                                    dateTimePicker.config.disabled = true;
                                    dateTimePicker.config.validate.required = false;
                                }
                                dateTimePicker.update();
                            }
                        },
                    },
                    style: {
                        align: 'left',
                        layout: '2-columns-stretch',
                    },
                },
                {
                    type: 'form.divider',
                },
                {
                    type: 'form.dateTimePicker',
                    key: 'sendDateTime',
                    dateLabel: t('wizard.schedule.sendDate'),
                    timeLabel: t('wizard.schedule.sendTime'),
                    timeFormat: '24h',
                    display: 'column',
                    timeMinuteStep: 15,
                    disabled: true,
                    validate: {
                        required: false,
                        minDate: roundUpToNearestFifteenMinutes(Date.now()),
                        minDateMessage: t('wizard.schedule.dateValidationMessage'),
                    },
                },
            ],
        },
        ...(mode === 'notification' || mode === 'draft'
            ? [
                {
                    type: 'form.panel',
                    style: {
                        surface: 'level1',
                    },
                    children: [
                        {
                            type: 'form.checkbox',
                            key: 'disableFields',
                            label: t('pages.summary.readyToSend'),
                            event: {
                                onChange: (value, handler) => {
                                    const panel = handler.up().up().up().down('!alertPanel');
                                    const dateTimePicker = handler.up().up().down('sendDateTime');
                                    const sendType = handler.up().up().down('sendType');
                                    handler.setShowErrorsOverride('all');
                                    if (panel) {
                                        panel.setDisabled(!!value, true);
                                    }
                                    if ((sendType === null || sendType === void 0 ? void 0 : sendType.getData()) === 'immediately' && dateTimePicker) {
                                        dateTimePicker.config.disabled = true;
                                    }
                                    if (value === true && dateTimePicker && sendType) {
                                        dateTimePicker.config.disabled = true;
                                        sendType.config.disabled = true;
                                    }
                                    else if (value === false && dateTimePicker && sendType) {
                                        dateTimePicker.config.disabled = false;
                                        sendType.config.disabled = false;
                                        if ((sendType === null || sendType === void 0 ? void 0 : sendType.getData()) === 'immediately') {
                                            dateTimePicker.config.disabled = true;
                                        }
                                    }
                                },
                            },
                            validate: {
                                required: true,
                                requiredMessage: t('wizard.schedule.fieldIsRequired'),
                            },
                        },
                    ],
                },
            ]
            : []),
    ];
}
function roundUpToNearestFifteenMinutes(timeStamp) {
    const date = new Date(timeStamp);
    const minutes = date.getMinutes();
    const remainder = minutes % 15;
    const addMinutes = remainder === 0 ? 0 : 15 - remainder;
    date.setMinutes(minutes + addMinutes);
    date.setSeconds(0);
    date.setMilliseconds(0);
    return date.getTime();
}
